import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FileText,
  ClipboardCheck,
  ScrollText,
  Compass,
  ArrowRight,
  Phone,
  Mail,
  CheckCircle,
} from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import IntakeModal from "../components/IntakeModal";
import { DIVISIONS } from "../lib/divisions";
import { VETERAN_SUB_SERVICES } from "../lib/veteranSubServices";

const categories = [
  {
    icon: FileText,
    slug: "evidence-development",
    title: "Evidence Development",
    services: VETERAN_SUB_SERVICES["evidence-development"].services.map((s) => s.title),
  },
  {
    icon: ClipboardCheck,
    slug: "appointment-preparation",
    title: "Appointment Preparation",
    services: VETERAN_SUB_SERVICES["appointment-preparation"].services.map((s) => s.title),
  },
  {
    icon: ScrollText,
    slug: "decision-review",
    title: "Decision Review",
    services: VETERAN_SUB_SERVICES["decision-review"].services.map((s) => s.title),
  },
  {
    icon: Compass,
    slug: "specialized-guidance",
    title: "Specialized Guidance",
    services: VETERAN_SUB_SERVICES["specialized-guidance"].services.map((s) => s.title),
  },
];

const VeteranServicesPage = () => {
  const [showIntake, setShowIntake] = useState(false);
  const div = DIVISIONS.veteran;

  return (
    <div className="pt-24">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0B2343] text-white">
        <div className="absolute inset-0 opacity-[0.06]">
          <img
            src="https://images.unsplash.com/photo-1676286155307-9fd53cd4b431?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxhbWVyaWNhbiUyMGZsYWclMjB0ZXh0dXJlJTIwY2xvc2UlMjB1cHxlbnwwfHx8fDE3Njk4Njg1NDZ8MA&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-8 py-20 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="h-[3px] w-14 bg-[#C89B3C] mb-6" />
            <span className="text-xs font-medium tracking-[0.2em] uppercase text-[#C89B3C] mb-3 block">
              Veteran Disability Consulting
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 max-w-3xl">
              Navigate VA claims with clarity, evidence, and strategy.
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed max-w-2xl mb-8">
              From record reviews and C&P prep to decision letter analysis and specialized
              guidance, we help veterans prepare well-supported claims — every step of the way.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setShowIntake(true)}
                data-testid="veteran-intake-cta"
                className="bg-[#C89B3C] text-white px-8 py-4 font-medium hover:bg-[#a8852f] transition-all inline-flex items-center justify-center gap-2"
              >
                Start Intake Form
                <ArrowRight size={18} />
              </button>
              <Link
                to="/schedule"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0B2343] px-8 py-4 font-medium transition-all inline-flex items-center justify-center gap-2"
              >
                Free Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <SectionHeading
            eyebrow="What we help with"
            title="Comprehensive support across four focus areas."
            subtitle="Every engagement is tailored to your service history, records, and goals — no cookie-cutter approach."
            align="center"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {categories.map((cat, idx) => {
              const Icon = cat.icon;
              return (
                <motion.div
                  key={cat.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: idx * 0.08 }}
                  className="bg-slate-50 p-8 border border-slate-200 hover:border-[#C89B3C] transition-colors flex flex-col"
                  data-testid={`veteran-category-${idx}`}
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-[#0B2343] flex items-center justify-center">
                      <Icon className="text-[#C89B3C]" size={26} />
                    </div>
                    <h3 className="font-serif text-xl font-bold text-slate-900">{cat.title}</h3>
                  </div>
                  <ul className="space-y-3 flex-1">
                    {cat.services.map((s) => (
                      <li key={s} className="flex items-start gap-2 text-slate-700">
                        <CheckCircle className="text-[#C89B3C] mt-0.5 flex-shrink-0" size={16} />
                        <span className="text-sm">{s}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/veteran-services/${cat.slug}`}
                    className="mt-6 pt-5 border-t border-slate-200 inline-flex items-center gap-2 text-[#0B2343] font-medium hover:text-[#C89B3C] transition-colors"
                    data-testid={`veteran-category-link-${cat.slug}`}
                  >
                    Learn more <ArrowRight size={16} />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-16 bg-amber-50 border-y border-amber-200">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <h3 className="font-serif text-xl font-bold text-slate-900 mb-3">Important Notice</h3>
          <p className="text-slate-700 leading-relaxed">
            Noble Strategies Consulting does not make guarantees regarding VA decisions or
            disability ratings. We are not a law firm, healthcare provider, or accredited VA
            representative unless separately stated. Our services are consultative, educational,
            and advocacy-based only.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Ready to build your strategy?
          </h2>
          <p className="text-slate-600 mb-8">
            Complete our 6-step intake form and we'll review your situation within 24–48 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={() => setShowIntake(true)}
              className="bg-[#0B2343] text-white px-8 py-4 font-medium hover:bg-slate-800 transition-colors"
              data-testid="veteran-intake-cta-bottom"
            >
              Start Veteran Intake Form
            </button>
            <a
              href={div.phoneHref}
              className="border-2 border-[#0B2343] text-[#0B2343] hover:bg-[#0B2343] hover:text-white px-8 py-4 font-medium transition-all inline-flex items-center justify-center gap-2"
            >
              <Phone size={18} />
              {div.phone}
            </a>
          </div>
          <a
            href={`mailto:${div.email}`}
            className="text-slate-600 hover:text-[#C89B3C] inline-flex items-center gap-2 text-sm"
          >
            <Mail size={14} />
            {div.email}
          </a>
        </div>
      </section>

      <IntakeModal isOpen={showIntake} onClose={() => setShowIntake(false)} />
    </div>
  );
};

export default VeteranServicesPage;
